import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
    Typography,
    Box,
    TextField,
    Button,
    Grid,
    Divider,
    FormControl,
    FormControlLabel,
    Switch,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from "@mui/material";
import { Lock as LockIcon, Email as EmailIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { RootState } from "../../../store/store";
import classes from "../Settings.module.scss";
import { ComingSoonOverlay } from "../../../components/ui/ComingSoonOverlay/ComingSoonOverlay";

const AccountTab: React.FC = () => {
    const { user } = useSelector((state: RootState) => state.auth);

    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [isChangingEmail, setIsChangingEmail] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

    // Password form state
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    });

    // Email form state
    const [emailForm, setEmailForm] = useState({
        currentEmail: user?.email || "example@maxaspire.com",
        newEmail: "",
    });

    // Password validation states
    const [passwordError, setPasswordError] = useState("");
    const [passwordSuccess, setPasswordSuccess] = useState("");

    // Email validation states
    const [emailError, setEmailError] = useState("");
    const [emailSuccess, setEmailSuccess] = useState("");

    // Handle password form input changes
    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setPasswordForm((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Clear errors when typing
        if (passwordError) setPasswordError("");
    };

    // Handle email form input changes
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setEmailForm((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Clear errors when typing
        if (emailError) setEmailError("");
    };

    // Toggle two-factor authentication
    const handleToggleTwoFactor = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTwoFactorEnabled(event.target.checked);
        // In a real app, this would trigger an API call to enable/disable 2FA
    };

    // Save password changes
    const savePassword = () => {
        // Validate password
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            setPasswordError("New passwords do not match");
            return;
        }

        if (passwordForm.newPassword.length < 8) {
            setPasswordError("Password must be at least 8 characters");
            return;
        }

        // Simulate API call
        setTimeout(() => {
            setPasswordSuccess("Password updated successfully");
            setPasswordForm({
                currentPassword: "",
                newPassword: "",
                confirmPassword: "",
            });

            // Hide success message after 3 seconds
            setTimeout(() => {
                setPasswordSuccess("");
                setIsChangingPassword(false);
            }, 3000);
        }, 1000);
    };

    // Save email changes
    const saveEmail = () => {
        // Validate email
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(emailForm.newEmail)) {
            setEmailError("Please enter a valid email address");
            return;
        }

        // Simulate API call
        setTimeout(() => {
            setEmailSuccess("Email updated successfully");
            setEmailForm({
                currentEmail: emailForm.newEmail,
                newEmail: "",
            });

            // Hide success message after 3 seconds
            setTimeout(() => {
                setEmailSuccess("");
                setIsChangingEmail(false);
            }, 3000);
        }, 1000);
    };

    // Handle account deletion request
    const handleDeleteAccount = () => {
        // In a real app, this would trigger an API call to delete the account
        setShowDeleteDialog(false);
        // Show some feedback or redirect to logout page
    };

    return (
        <div>
            <div className={classes.contentHeader}>
                <Typography variant="h5" component="h1">
                    Account Settings
                </Typography>
                <Typography variant="body2">
                    Manage your account security, login information, and account preferences
                </Typography>
            </div>

            {/* Password Section */}
            <div className={classes.infoCard} style={{ position: "relative" }}>
                <div className={classes.infoHeader}>
                    <Box display="flex" alignItems="center">
                        <LockIcon sx={{ mr: 1, color: "var(--primary-color)" }} />
                        <Typography variant="h6">Password</Typography>
                    </Box>
                    {!isChangingPassword && (
                        <>
                            <Button
                                size="small"
                                onClick={() => setIsChangingPassword(true)}
                                disabled={true}
                                sx={{
                                    borderColor: "var(--border-color)",
                                    color: "var(--text-secondary-color)",
                                    "&:hover": {
                                        borderColor: "var(--border-color)",
                                        backgroundColor: "var(--hover-background)",
                                    },
                                    opacity: 0.6,
                                }}
                            >
                                Change Password
                            </Button>
                        </>
                    )}
                </div>

                {isChangingPassword ? (
                    <Box component="form" sx={{ mt: 2, opacity: 0.6 }}>
                        {passwordError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {passwordError}
                            </Alert>
                        )}
                        {passwordSuccess && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {passwordSuccess}
                            </Alert>
                        )}

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Current Password"
                                    name="currentPassword"
                                    type="password"
                                    value={passwordForm.currentPassword}
                                    onChange={handlePasswordChange}
                                    variant="outlined"
                                    size="small"
                                    disabled={true}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": {
                                                borderColor: "var(--border-color)",
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "var(--primary-color)",
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "var(--primary-color)",
                                            },
                                            "& input": {
                                                color: "var(--text-color)",
                                                backgroundColor: "var(--input-background)",
                                            },
                                        },
                                        "& .MuiInputLabel-root": {
                                            color: "var(--text-secondary-color)",
                                        },
                                        "& .MuiInputLabel-root.Mui-focused": {
                                            color: "var(--primary-color)",
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="New Password"
                                    name="newPassword"
                                    type="password"
                                    value={passwordForm.newPassword}
                                    onChange={handlePasswordChange}
                                    variant="outlined"
                                    size="small"
                                    disabled={true}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": {
                                                borderColor: "var(--border-color)",
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "var(--primary-color)",
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "var(--primary-color)",
                                            },
                                            "& input": {
                                                color: "var(--text-color)",
                                                backgroundColor: "var(--input-background)",
                                            },
                                        },
                                        "& .MuiInputLabel-root": {
                                            color: "var(--text-secondary-color)",
                                        },
                                        "& .MuiInputLabel-root.Mui-focused": {
                                            color: "var(--primary-color)",
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Confirm New Password"
                                    name="confirmPassword"
                                    type="password"
                                    value={passwordForm.confirmPassword}
                                    onChange={handlePasswordChange}
                                    variant="outlined"
                                    size="small"
                                    disabled={true}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": {
                                                borderColor: "var(--border-color)",
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "var(--primary-color)",
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "var(--primary-color)",
                                            },
                                            "& input": {
                                                color: "var(--text-color)",
                                                backgroundColor: "var(--input-background)",
                                            },
                                        },
                                        "& .MuiInputLabel-root": {
                                            color: "var(--text-secondary-color)",
                                        },
                                        "& .MuiInputLabel-root.Mui-focused": {
                                            color: "var(--primary-color)",
                                        },
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setIsChangingPassword(false);
                                    setPasswordError("");
                                    setPasswordSuccess("");
                                }}
                                sx={{ mr: 1 }}
                                disabled={true}
                            >
                                Cancel
                            </Button>
                            <Button variant="contained" onClick={savePassword} disabled={true}>
                                Save Changes
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ mt: 1, opacity: 0.6 }}>
                        <Typography variant="body2">
                            For better security, change your password often.
                        </Typography>
                    </Box>
                )}
            </div>

            {/* Email Section */}
            <Box mb={4}>
                <Box display="flex" alignItems="center" mb={2}>
                    <EmailIcon sx={{ mr: 1, color: "var(--primary-color)" }} />
                    <Typography variant="h6">Email Address</Typography>
                </Box>

                {!isChangingEmail ? (
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="body1">Your current email address</Typography>
                            <Typography variant="body2" fontWeight="bold">
                                {emailForm.currentEmail}
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined"
                            onClick={() => setIsChangingEmail(true)}
                            sx={{
                                borderColor: "var(--primary-color)",
                                color: "var(--primary-color)",
                                "&:hover": {
                                    borderColor: "var(--primary-dark)",
                                    backgroundColor: "var(--hover-background)",
                                },
                                opacity: 0.6,
                            }}
                            disabled={true}
                        >
                            Change Email (Beta)
                        </Button>
                    </Box>
                ) : (
                    <Box>
                        {emailError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {emailError}
                            </Alert>
                        )}

                        {emailSuccess && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {emailSuccess}
                            </Alert>
                        )}

                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Current Email"
                                    fullWidth
                                    disabled
                                    value={emailForm.currentEmail}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": {
                                                borderColor: "var(--border-color)",
                                            },
                                            "& input": {
                                                color: "var(--text-secondary-color)",
                                                backgroundColor: "var(--input-background)",
                                            },
                                        },
                                        "& .MuiInputLabel-root": {
                                            color: "var(--text-secondary-color)",
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    name="newEmail"
                                    label="New Email"
                                    type="email"
                                    fullWidth
                                    value={emailForm.newEmail}
                                    onChange={(e) => {
                                        setEmailForm((prev) => ({
                                            ...prev,
                                            newEmail: e.target.value,
                                        }));
                                        if (emailError) setEmailError("");
                                    }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": {
                                                borderColor: "var(--border-color)",
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "var(--primary-color)",
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "var(--primary-color)",
                                            },
                                            "& input": {
                                                color: "var(--text-color)",
                                                backgroundColor: "var(--input-background)",
                                            },
                                        },
                                        "& .MuiInputLabel-root": {
                                            color: "var(--text-secondary-color)",
                                        },
                                        "& .MuiInputLabel-root.Mui-focused": {
                                            color: "var(--primary-color)",
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} display="flex" justifyContent="flex-end">
                                <Button
                                    onClick={() => {
                                        setIsChangingEmail(false);
                                        setEmailError("");
                                    }}
                                    sx={{
                                        mr: 2,
                                        color: "var(--text-color)",
                                        "&:hover": {
                                            backgroundColor: "var(--hover-background)",
                                        },
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={saveEmail}
                                    sx={{
                                        backgroundColor: "var(--primary-color)",
                                        "&:hover": {
                                            backgroundColor: "var(--primary-dark)",
                                        },
                                    }}
                                >
                                    Save Changes
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 3, borderColor: "var(--border-color)" }} />

            {/* Two-Factor Authentication */}
            <Box mb={4}>
                <Box display="flex" alignItems="center" mb={2}>
                    <LockIcon sx={{ mr: 1, color: "var(--primary-color)" }} />
                    <Typography variant="h6">Two-Factor Authentication</Typography>
                </Box>

                <Box
                    className={classes.switchContainer}
                    sx={{ opacity: 0.8, position: "relative" }}
                >
                    <ComingSoonOverlay
                        message="2FA security enhancement launching soon!"
                        link="https://docs.google.com/forms/d/e/1FAIpQLSeTuQwmG-JMFXfbdgLfxFIp2mKgjQs6RD9-pkjOevZYFN-BCw/viewform?usp=dialog"
                    />
                    <Box>
                        <Typography variant="body1">Enable Two-Factor Authentication</Typography>
                        <Typography variant="body2" color="textSecondary">
                            Add an extra layer of security to your account
                        </Typography>
                    </Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={twoFactorEnabled}
                                onChange={(e) => setTwoFactorEnabled(e.target.checked)}
                                disabled={true}
                                color="primary"
                            />
                        }
                        label=""
                    />
                </Box>
            </Box>

            <Divider sx={{ my: 3, borderColor: "var(--border-color)" }} />

            {/* Delete Account */}
            <Box mb={4}>
                <Box display="flex" alignItems="center" mb={2}>
                    <DeleteIcon sx={{ mr: 1, color: "error.main" }} />
                    <Typography variant="h6">Delete Account</Typography>
                </Box>

                <Box sx={{ bgcolor: "error.main", p: 2, borderRadius: 1, opacity: 0.9 }}>
                    <p
                        style={{
                            color: "white",
                            fontWeight: "bold",
                            margin: 0,
                            fontSize: "1rem",
                            lineHeight: "1.5",
                        }}
                    >
                        Warning: This action cannot be undone
                    </p>
                    <p
                        style={{
                            color: "white",
                            marginTop: "8px",
                            marginBottom: 0,
                            fontSize: "0.875rem",
                            lineHeight: "1.43",
                        }}
                    >
                        Deleting your account will permanently remove all your data from our
                        platform.
                    </p>
                    <Box sx={{ mt: 2 }}>
                        <Button
                            variant="outlined"
                            color="inherit"
                            onClick={() => setShowDeleteDialog(true)}
                            disabled={true}
                            sx={{
                                borderColor: "rgba(255, 255, 255, 0.3)",
                                color: "rgba(255, 255, 255, 0.5)",
                                "&:hover": {
                                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                                    borderColor: "rgba(255, 255, 255, 0.3)",
                                },
                                opacity: 0.6,
                            }}
                        >
                            Delete Account (Beta)
                        </Button>
                    </Box>
                </Box>

                {/* Delete Account Dialog */}
                <Dialog
                    open={showDeleteDialog}
                    onClose={() => setShowDeleteDialog(false)}
                    PaperProps={{
                        sx: {
                            backgroundColor: "var(--card-background)",
                            color: "var(--text-color)",
                            borderRadius: "8px",
                        },
                    }}
                >
                    <DialogTitle sx={{ color: "var(--text-color)" }}>
                        Are you sure you want to delete your account?
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ color: "var(--text-secondary-color)" }}>
                            This action cannot be undone. All of your data will be permanently
                            deleted. Please type your password to confirm.
                        </DialogContentText>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Password"
                            type="password"
                            fullWidth
                            variant="outlined"
                            sx={{
                                mt: 2,
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: "var(--border-color)",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "error.main",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "error.main",
                                    },
                                    "& input": {
                                        color: "var(--text-color)",
                                        backgroundColor: "var(--input-background)",
                                    },
                                },
                                "& .MuiInputLabel-root": {
                                    color: "var(--text-secondary-color)",
                                },
                                "& .MuiInputLabel-root.Mui-focused": {
                                    color: "error.main",
                                },
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button
                            onClick={() => setShowDeleteDialog(false)}
                            sx={{
                                color: "var(--text-color)",
                                "&:hover": {
                                    backgroundColor: "var(--hover-background)",
                                },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="error"
                            variant="contained"
                            onClick={() => {
                                // This would call the delete account API
                                setShowDeleteDialog(false);
                                // Show success message or redirect to landing page
                            }}
                            disabled={true}
                            sx={{ opacity: 0.6 }}
                        >
                            Delete Forever
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </div>
    );
};

export default AccountTab;
